import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Paper, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import ActionButton from '../../../components/ActionButton';
import ReminderFormFields, {
  ReminderDescription,
  ReminderRemoveConfirmDialog,
} from './ReminderFormFields';
import { REMINDER_STYLES } from './reminderConstants';
import {
  areRemindersEqual,
  isReminderEmpty,
  isReminderPartial,
  normalizeReminder,
} from './reminderUtils';

const useStyles = makeStyles((theme) => ({
  section: {
    width: '85%',
    maxWidth: '1520px',
    margin: `${theme.spacing(4)}px auto 0`,
  },
  card: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
    padding: theme.spacing(3),
    borderRadius: '8px',
    backgroundColor: '#f9f9f9',
    boxShadow: '0px 2px 4px rgba(0,0,0,0.1)',
    textAlign: 'left',
  },
  header: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },
  removeButton: {
    alignSelf: 'flex-start',
    minHeight: 36,
  },
  removeButtonHidden: {
    visibility: 'hidden',
    pointerEvents: 'none',
  },
}));

const ReminderSection = ({
  onChange,
  onRemove,
  reminderMessage,
  reminderStyle,
}) => {
  const classes = useStyles();
  const [isConfirmRemoveOpen, setIsConfirmRemoveOpen] = useState(false);
  const [draftStyle, setDraftStyle] = useState(reminderStyle ?? null);
  const [draftMessage, setDraftMessage] = useState(reminderMessage ?? '');

  // Last value emitted to the parent. The parent stores the normalized pair
  // (nulls while empty/partial) and feeds it back down; without this guard the
  // effect below would re-sync that echo and clobber in-progress edits (e.g. a
  // selected style with no message yet).
  const emittedRef = useRef({ reminderStyle, reminderMessage });

  useEffect(() => {
    const incoming = { reminderStyle, reminderMessage };
    if (areRemindersEqual(incoming, emittedRef.current)) {
      return;
    }
    emittedRef.current = incoming;
    setDraftStyle(reminderStyle ?? null);
    setDraftMessage(reminderMessage ?? '');
  }, [reminderStyle, reminderMessage]);

  const notifyChange = (nextStyle, nextMessage) => {
    const normalized = normalizeReminder(nextStyle, nextMessage);
    emittedRef.current = normalized;

    onChange(normalized.reminderStyle, normalized.reminderMessage, {
      isPartial: isReminderPartial(nextStyle, nextMessage),
    });
  };

  const handleStyleChange = (value) => {
    setDraftStyle(value);
    notifyChange(value, draftMessage);
  };

  const handleMessageChange = (value) => {
    setDraftMessage(value);
    notifyChange(draftStyle, value);
  };

  const handleRemove = () => {
    setDraftStyle(null);
    setDraftMessage('');
    emittedRef.current = { reminderStyle: null, reminderMessage: null };
    onRemove();
  };

  const hasReminderContent = !isReminderEmpty(draftStyle, draftMessage);

  return (
    <div className={classes.section}>
      <Paper elevation={0} className={classes.card}>
        <div className={classes.header}>
          <Typography variant="h5">Promemoria</Typography>
          <ReminderDescription />
        </div>

        <ReminderFormFields
          reminderStyle={draftStyle}
          reminderMessage={draftMessage}
          onStyleChange={handleStyleChange}
          onMessageChange={handleMessageChange}
        />

        <div
          aria-hidden={!hasReminderContent}
          data-testid="reminder-remove-slot"
          className={`${classes.removeButton} ${
            hasReminderContent ? '' : classes.removeButtonHidden
          }`}
        >
          <ActionButton
            label="RIMUOVI"
            secondaryButton
            disabled={!hasReminderContent}
            disableMargin={true}
            onClick={() => setIsConfirmRemoveOpen(true)}
          />
        </div>
      </Paper>

      <ReminderRemoveConfirmDialog
        open={isConfirmRemoveOpen}
        onCancel={() => setIsConfirmRemoveOpen(false)}
        onConfirm={() => {
          setIsConfirmRemoveOpen(false);
          handleRemove();
        }}
      />
    </div>
  );
};

ReminderSection.propTypes = {
  onChange: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  reminderMessage: PropTypes.string,
  reminderStyle: PropTypes.oneOf([
    ...REMINDER_STYLES.map((item) => item.value),
    null,
  ]),
};

export default ReminderSection;
